import { motion, Variants } from "framer-motion";
import { Icon } from "src/components/atoms/Icon";
import { Css, Palette } from "~generated/css";
import { useTestIds } from "~utils";

type RadioProps = {
  selected: boolean;
  onClick?: () => void;
  disabled?: boolean | string;
  variant?: "primary" | "secondary" | "primaryPlus";
  // Will add an `ifContainer` to css to reduce size of radio from 24px -> 20px
  containerBreakpoint?: number;
};

const radioPrimaryVariants: Variants = {
  selected: (disabled?: boolean) => ({
    backgroundColor: disabled ? Palette.Gray8 : Palette.Gray800,
    borderColor: disabled ? Palette.LightGray : Palette.Black,
    transition: {
      duration: 0.2,
    },
  }),
  unselected: (disabled?: boolean) => ({
    backgroundColor: disabled ? Palette.Gray8 : Palette.White,
    borderColor: Palette.LightGray,
    transition: {
      duration: 0.2,
    },
  }),
};

export function Radio(props: RadioProps) {
  const { selected, onClick, disabled, variant = "primary", containerBreakpoint } = props;
  const tid = useTestIds(props, "radio-checkbox");
  const isPrimary = variant === "primary" || variant === "primaryPlus";
  return (
    <motion.button
      {...tid}
      type="button"
      onClick={() => onClick?.()}
      custom={disabled}
      variants={radioPrimaryVariants}
      initial={false}
      animate={selected ? "selected" : "unselected"}
      disabled={!!disabled}
      css={{
        ...Css.df.jcc.aic.wPx(24).hPx(24).br16.ba.bcGray800.if(!!disabled).cursorNotAllowed.$,
        ...Css.if(variant === "primaryPlus").bcBlack.else.bcLightGray.$,
        ...(containerBreakpoint && Css.ifContainer({ lt: containerBreakpoint }).wPx(20).hPx(20).$),
        // Style all our disabled icons to the same color without adding additional icons for each
        ...Css.addIn("span svg path", Css.if(!!disabled).fGray.$).$,
      }}
      data-selected={selected}
    >
      {variant === "primaryPlus" ? (
        <motion.span initial={false}>
          <Icon name={selected ? "checkmark-white" : "plus-black"} />
        </motion.span>
      ) : (
        <motion.span
          initial={false}
          variants={{
            selected: { opacity: 1, transition: { duration: 0.2 } },
            unselected: { opacity: 0, transition: { duration: 0.2 } },
          }}
        >
          <Icon name={isPrimary ? "checkmark-white" : "checkmark-black"} />
        </motion.span>
      )}
    </motion.button>
  );
}
